import type { ReactNode } from 'react'
import React, { useCallback, useContext, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import loadable from 'components/utils/lazyImport'

import MarkdownRender from 'LuxKit/MarkdownRender'
import NavigationBar from './NavigableOfferContentBar'
import TraderInformation from './TraderInformation'
import PropertyFinePrint from 'components/Common/BedbankPropertyFinePrint'
import LayoutContainer from 'components/Common/LayoutContainer'
import { PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import { scrollToId } from 'lib/window/scrollUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import NavigableOfferContentGroup from './NavigableOfferContentGroup'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import Modal from 'LuxKit/Modal/Modal'
import UserReviewList from './UserReviewList'
import UserReviewCarousel from './UserReviewCarousel'
import TextLink from 'LuxKit/TextLink'
import { OptimizelyExperiments } from 'constants/optimizely'
import { useDeferredAnalyticsOptimizely } from 'lib/optimizely/useDeferredAnalyticsOptimizely'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Sheet from 'components/utils/Sheet'
import Divider from 'LuxKit/Divider'
import ModalContext from 'contexts/ModalContext'
import { trackClientEvent } from 'analytics/analytics'

const PropertyMap = loadable(() => import(/* webpackChunkName: "PropertyMap" */ 'components/Common/PropertyMap'))

interface Props {
  hash?: string
  offer: App.BedbankOffer
  traderInformation?: App.TraderInformation
}

const TwoColumns = styled.div`
  ${mediaQueryUp.tablet} {
    columns: 2;

    > * {
      break-inside: avoid;
    }
  }
`

const FacilitiesList = styled.ul`
  list-style-type: none;
  margin: 0;
`

const Category = styled.li`
  display: flex;
  align-items: center;
  margin: 0 0 ${rem(16)};

  & > svg {
    min-width: ${rem(20)};
    margin-right: ${rem(12)};
  }
`

const HighlightsWrapper = styled.div`
  & > * + * {
    margin-top: ${rem(16)};
  }
`

const READ_MORE_TRACKING_MAP: Record<string, string> = {
  'getting-there': 'offer_page_read_more_location',
  highlights: 'offer_page_read_more_highlights',
  facilities: 'offer_page_read_more_facilities',
  'fine-print': 'offer_page_read_more_fine_print',
}

function offerHasCoordinates(offer: App.BedbankOffer): boolean {
  return !!(offer.property?.latitude && offer.property.longitude)
}

interface ContentField {
  id: string
  label: string | ReactNode
  open: boolean
  initialHeight?: number
  minimumContentHeight?: number
  contentComponent: React.ReactElement
}

function getContentFields(props: Props, isNewReviewsPanelEnabled: boolean, intl: ReturnType<typeof useIntl>): Array<ContentField> {
  const {
    hash,
    offer,
  } = props

  const {
    property: {
      finePrint,
      finePrintOverride,
      location,
      id,
      longitude,
      latitude,
      aboutThisProperty,
      attractionsOverride,
      highlights,
      facilitiesOverride,
    },
    promotions,
  } = offer

  const hasPromotions = offer.promotions.length > 0

  const promoFinePrint: string | null = promotions.filter(p => p.finePrint).map(p => p.finePrint)?.at(0) || null

  const contentFields: Array<ContentField> = []

  if (offerHasCoordinates(offer)) {
    contentFields.push({
      id: 'getting-there',
      label: intl.formatMessage({
        id: 'WR071s0Z7f',
        defaultMessage: 'Location',
        description: 'Location section label for bedbank navigable offer content',
      }),
      open: hash === '#getting-there',
      initialHeight: 410,
      contentComponent: <VerticalSpacer gap={16}>
        <PropertyMap
          height={316}
          wrapperStyle={{ height: 316 }}
          markers={[{
            id,
            type: PROPERTY_MAP_MARKER_TYPE,
            latitude,
            longitude,
          }]}
        />
        <BodyText variant="medium" weight="bold" data-testid="offer-location">{location}</BodyText>
        {offer.attractions &&
          <div>
            <BodyText variant="medium" weight="bold" data-testid="offer-location">
              {intl.formatMessage({
                id: 'CeMuEgelp0',
                defaultMessage: "What's nearby",
                description: "What's nearby section heading for bedbank navigable offer content",
              })}
            </BodyText>
            <MarkdownRender fontSize="medium" content={attractionsOverride ?? offer.attractions} />
          </div>}
      </VerticalSpacer>,
    })
  }

  if (hasPromotions && (highlights || aboutThisProperty?.length)) {
    contentFields.push({
      id: 'highlights',
      label: intl.formatMessage({
        id: 'JLm5R9CQqK',
        defaultMessage: 'Highlights',
        description: 'Highlights section label for bedbank navigable offer content',
      }),
      open: hash === '#highlights',
      initialHeight: 120,
      contentComponent: <HighlightsWrapper>
        {highlights && <MarkdownRender fontSize="medium" content={highlights} />}
        {!highlights && aboutThisProperty?.map(({ name, values }) => (<div key={name}>
          <BodyText variant="medium" weight="bold">{name}</BodyText>
          {values.map((value, index) => (
            <MarkdownRender fontSize="medium" key={index} content={value} />
          ))}
        </div>))}
      </HighlightsWrapper>,
    })
  }

  if (offer.facilityGroups || facilitiesOverride) {
    contentFields.push({
      id: 'facilities-all',
      label: intl.formatMessage({
        id: 'ymaaFTh8Rt',
        defaultMessage: 'Facilities',
        description: 'Facilities section label for bedbank navigable offer content',
      }),
      open: hash === '#facilities-all',
      initialHeight: 120,
      contentComponent: <TwoColumns>
        {facilitiesOverride && (
          <div data-testid="offer-location">
            <MarkdownRender fontSize="medium" content={facilitiesOverride} />
          </div>
        )}
        {!facilitiesOverride && offer.facilityGroups.map(facilityGroup => (
          <div key={facilityGroup.name}>
            <BodyText variant="medium" weight="bold" data-testid="offer-location">{facilityGroup.name}</BodyText>
            <FacilitiesList>
              {facilityGroup.values.map(value => (
                <li key={value.name}>{value.name}</li>
              ))}
            </FacilitiesList>
          </div>
        ))}
      </TwoColumns>,
    })
  }

  if (finePrint?.safety?.length) {
    contentFields.push({
      id: 'fine-print-safety',
      label: intl.formatMessage({
        id: 'CbUHWUpXyo',
        defaultMessage: 'Cleaning and safety measures',
        description: 'Cleaning and safety measures section label for bedbank navigable offer content',
      }),
      contentComponent: <TwoColumns>
        {finePrint.safety.map(safetyAttribute => (
          <Category key={safetyAttribute}>
            <LineCheckIcon size="S" />{safetyAttribute}
          </Category>
        ))}
      </TwoColumns>,
      open: hash === '#fine-print-safety',
    })
  }

  if (finePrintOverride) {
    contentFields.push({
      id: 'fine-print',
      label: intl.formatMessage({
        id: 'g/Q0T+eUN5',
        defaultMessage: 'Fine print',
        description: 'Fine print section label for bedbank navigable offer content',
      }),
      contentComponent: <MarkdownRender type="compact" fontSize="medium" content={finePrintOverride} />,
      open: hash === '#fine-print',
    })
  } else if (finePrint || promoFinePrint) {
    contentFields.push({
      id: 'fine-print',
      label: intl.formatMessage({
        id: 'g/Q0T+eUN5',
        defaultMessage: 'Fine print',
        description: 'Fine print section label for bedbank navigable offer content',
      }),
      contentComponent: <>
        {promoFinePrint &&
          <MarkdownRender content={promoFinePrint} type="compact" fontSize="medium" />}
        {!promoFinePrint && finePrint && Object.keys(finePrint).length > 0 &&
          <PropertyFinePrint
            finePrint={finePrint}
          />}
      </>,
      open: hash === '#fine-print',
    })
  }

  if (offer.property?.rating?.reviewsTotal && offer.property?.rating.score) {
    contentFields.push({
      id: 'customer-reviews',
      label: intl.formatMessage({
        id: 'DWFLDJmGS1',
        defaultMessage: 'Reviews',
        description: 'Reviews section label for bedbank navigable offer content',
      }),
      open: hash === '#customer-reviews',
      initialHeight: 200,
      minimumContentHeight: 2000,
      contentComponent: isNewReviewsPanelEnabled
        ? <UserReviewCarousel property={offer.property} isLPP />
        : <UserReviewList property={offer.property} isLPP />,
    })
  }

  return contentFields
}

function BedbankNavigableOfferContent(props: Props) {
  const intl = useIntl()
  const { variation: reviewsPanelVariation, triggerAnalytics: triggerReviewsExperiment } =
    useDeferredAnalyticsOptimizely(OptimizelyExperiments.croNewReviewsPanel)
  const isNewReviewsPanelEnabled = !!reviewsPanelVariation

  const contentFields = getContentFields(props, isNewReviewsPanelEnabled, intl)
  const defaultOpenField = contentFields.find(field => field.open)
  const [openFields, setOpenFields] = useState<Record<string, boolean>>(defaultOpenField ? { [defaultOpenField.id]: true } : {})
  const [headerSelected, setHeaderSelected] = useState<string | undefined>(defaultOpenField ? defaultOpenField?.id : undefined)

  const onGroupInView = useCallback((id: string, inView: boolean) => {
    if (inView) {
      setHeaderSelected(id)
      if (id === 'customer-reviews') {
        triggerReviewsExperiment()
      }
    }
  }, [triggerReviewsExperiment])

  const setFieldToggle = useCallback((id: string, toggled: boolean) => {
    window.setTimeout(() => {
      scrollToId(id, -74)
    }, 300)

    setOpenFields({
      ...openFields,
      [id]: toggled,
    })
  }, [openFields])

  const onHeaderClick = useCallback((id: string) => {
    setFieldToggle(id, true)
  }, [setFieldToggle])

  const onReadMoreToggle = useCallback((id: string) => {
    if (READ_MORE_TRACKING_MAP[id]) {
      const isOpening = !openFields[id]
      const subject = isOpening ? READ_MORE_TRACKING_MAP[id] : `${READ_MORE_TRACKING_MAP[id]}_close`
      trackClientEvent({
        subject,
        action: 'click',
        category: 'offer page',
        type: 'interaction',
      })
    }
    setFieldToggle(id, !openFields[id])
  }, [setFieldToggle, openFields])

  const showModal = useContext(ModalContext)

  const onModalOpenToggle = useCallback(() => {
    showModal(<Modal
      title={intl.formatMessage({
        id: 'V/wdSIwSmF',
        defaultMessage: 'About this supplier',
        description: 'Modal title for trader information in bedbank navigable offer content',
      })}
    >
      <TraderInformation traderInformation={props.traderInformation!} />
    </Modal>)
  }, [showModal, props.traderInformation, intl])

  return (
    <>
      <NavigationBar
        contentFields={contentFields}
        onClick={onHeaderClick}
        selectedHeader={headerSelected}
      />
      <LayoutContainer variant="medium">
        <Sheet padding="20 0" tabletPadding="56 0">
          <VerticalSpacer gap={20}>
            {contentFields.map((contentField, index) => <React.Fragment key={contentField.id}>
              {index !== 0 && <Divider kind="secondary" direction="horizontal" />}
              <NavigableOfferContentGroup
                heading={contentField.label}
                id={contentField.id}
                onInViewChange={onGroupInView}
                initialHeight={contentField.initialHeight ?? 96}
                minimumContentHeight={contentField.minimumContentHeight ?? 0}
                open={!!openFields[contentField.id]}
                onToggle={onReadMoreToggle}
              >
                <BodyText variant="medium">
                  {contentField.contentComponent}
                </BodyText>
              </NavigableOfferContentGroup>
            </React.Fragment>)}
            {props.traderInformation && (
              <div id="trader-information">
                <TextLink size="medium" onClick={onModalOpenToggle}>
                  {intl.formatMessage({
                    id: 'V/wdSIwSmF',
                    defaultMessage: 'About this supplier',
                    description: 'Modal title for trader information in bedbank navigable offer content',
                  })}
                </TextLink>
              </div>
            )}
          </VerticalSpacer>
        </Sheet>
      </LayoutContainer>
    </>
  )
}

export default BedbankNavigableOfferContent
